import useSWR from 'swr';
import { useAuth } from '@/contexts/AuthContext';
import { usageService } from '@/services/usageService';
import { UsageResponse } from '@/types/usage';
import { useCallback } from 'react';
import { debugLog } from '@/lib/debug-log';

export function useUserUsage() {
  const { user } = useAuth();

  const { data, error, isLoading, mutate } = useSWR<UsageResponse>(
    user?.uid ? ['userUsage', user.uid] : null,
    async () => {
      if (!user?.uid) throw new Error('User not authenticated');
      const result = await usageService.getUserUsage(user.uid);
      return result;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000, // 5 minutes
      errorRetryCount: 3,
      onSuccess: (result) => {
        debugLog('usage-fetch-success', {
          user_id: user?.uid,
          source: result.usage_usd?.source,
          active_budget_usd: result.usage_usd?.active_budget_usd,
          active_spent_usd: result.usage_usd?.active_spent_usd,
          active_remaining_usd: result.usage_usd?.active_remaining_usd,
          bonus_balance_usd: result.usage_usd?.bonus_balance_usd,
          total_available_usd: result.usage_usd?.total_available_usd,
          would_block_usd: result.usage_usd?.would_block_usd,
        });
      },
      onError: (fetchError) => {
        debugLog('usage-fetch-error', {
          user_id: user?.uid,
          error:
            fetchError instanceof Error ? fetchError.message : String(fetchError),
        });
      },
    }
  );

  // Function to manually refresh usage data
  const refreshUsage = useCallback(() => {
    debugLog('usage-refresh', { user_id: user?.uid });
    mutate();
  }, [mutate, user?.uid]);

  // Optimistic update: patch local cache immediately, revalidate in background
  const optimisticUpdate = useCallback(
    (updater: (current: UsageResponse) => UsageResponse) => {
      mutate(
        (current) => (current ? updater(current) : current),
        { revalidate: false }
      );
    },
    [mutate]
  );

  return {
    data,
    error,
    isLoading,
    refreshUsage,
    optimisticUpdate,
  };
}
